import { Badge } from '@dixa/lib-shared-habla';
import { Message as MessageT } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';

import { ConversationAuditLogEntry } from 'apis/auditLogs';
import { getMessageSanitizationChannel } from 'app/utils/htmlSanitizer';
import { useTranslatePlainTextConversationMessage } from 'assets/components/conversation/components/messages/utils/useTranslatePlainTextConversationMessage';
import { TranslationBadge } from 'components/translationBadge';

import { Message } from '../../../conversation/components/message';

import { getLogEntryAuthorName, LogEntry } from './logEntry';
import styles from './logEntry/logEntry.module.scss';
import { SidekickReplyDebugButton } from './sidekickDebugButton/sidekickReplyDebugButton';
import { UserLink } from './userLink/userLink';

type MessageAddedProps = {
  entry: ConversationAuditLogEntry<
    'MESSAGE_ADDED_BY_AGENT' | 'MESSAGE_ADDED_BY_CUSTOMER'
  >;
  messages: Array<MessageT>;
};

type MessageAddedContentProps = {
  message: MessageT;
  author: User;
  isTranscript: boolean;
};

const MessageAddedContent = ({
  message,
  author,
  isTranscript,
}: MessageAddedContentProps): JSX.Element => {
  const { button: translateButton, translation } =
    useTranslatePlainTextConversationMessage({
      text: message.text ?? undefined,
      originalLanguageCode: message.language ?? null,
      contentType: message.content_type === 'Html' ? 'html' : 'text',
    });

  const displayMarkup =
    translation?.status === 'fulfilled' && translation.translatedText
      ? translation.translatedText
      : (message.text ?? undefined);

  const nameMenu =
    isTranscript || translation?.status ? (
      <>
        {isTranscript && (
          <Badge size="small" variant="info">
            Transcribed
          </Badge>
        )}
        {translation?.status && (
          <TranslationBadge
            translation={translation}
            originalLanguageCode={message.language ?? undefined}
            errorMessage="There was an error translating this message."
          />
        )}
      </>
    ) : undefined;

  return (
    <Message
      className={styles.message}
      author={author}
      markup={displayMarkup}
      date=""
      channel={getMessageSanitizationChannel(message)}
      nameMenu={nameMenu}
      removeGutter
      gutterButtons={translateButton}
    />
  );
};

export const MessageAdded = ({
  entry,
  messages,
}: MessageAddedProps): JSX.Element => {
  const authorName = getLogEntryAuthorName(entry);
  const authorLink = <UserLink userId={entry.author.id}>{authorName}</UserLink>;

  const userType =
    entry.activity_type === 'MESSAGE_ADDED_BY_AGENT' ? 'agent' : 'customer';

  const text = (
    <>
      Message is added by {userType} {authorLink}
    </>
  );
  const author: User = {
    id: entry.author.id,
    name: entry.author.name ?? null,
    email: entry.author.email ?? null,
    phone_number: entry.author.phone_number ?? null,
    avatar_url: entry.avatar_url ?? null,
    type: userType === 'agent' ? 'member' : 'contact',
    org_id: null,
    company_id: null,
    default_outbound_number: null,
    anonymized_at: null,
    given_name: null,
    family_name: null,
    has_scim_enabled: false,
    external_id: null,
  };

  const message = messages.find((message) => message.id === entry.message_id);
  // Entries logged before the audit log carried is_transcript fall back to the loaded message.
  const isTranscript = Boolean(entry.is_transcript ?? message?.is_transcript);

  return (
    <LogEntry
      entry={entry}
      text={text}
      actions={
        userType === 'agent' && message?.text ? (
          <SidekickReplyDebugButton
            authorId={entry.author.id}
            csid={entry.resource.id}
            messageId={entry.message_id}
            date={entry.event_timestamp}
          />
        ) : undefined
      }
    >
      {message?.text ? (
        <MessageAddedContent
          message={message}
          author={author}
          isTranscript={isTranscript}
        />
      ) : null}
    </LogEntry>
  );
};
